/**
 * The button that opens a medication's source photos.
 *
 * Shared by the draft card and the saved card for the same reason the viewer is:
 * it is one action on one set of photos, and the nurse should not have to
 * recognise it twice because the row moved from one list to the other. The first
 * version of the saved card copied only the base icon and lost the magnifier
 * badge — which is exactly the drift this exists to stop.
 *
 * The badge is decorative; the Pressable carries the accessibility label. It
 * says the button does not merely show the photo, it opens it big enough to read
 * a strength off the label.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts } from "@/lib/theme";

interface Props {
    onPress: () => void;
    /** Photo count. Rendered only when more than one — a wrapped label. */
    count?: number;
    /**
     * The extraction has not finished working out which photos this medication
     * came from. Shows a spinner in place of the magnifier and blocks the press,
     * rather than opening a viewer that would come back empty.
     */
    pending?: boolean;
    accessibilityLabel: string;
}

export function SourcePhotoButton({
    onPress,
    count = 1,
    pending = false,
    accessibilityLabel
}: Props) {
    return (
        <Pressable
            onPress={onPress}
            disabled={pending}
            hitSlop={8}
            style={[ss.imageButton, pending && ss.imageButtonWaiting]}
            accessibilityRole="button"
            accessibilityState={{ disabled: pending }}
            accessibilityLabel={accessibilityLabel}
        >
            <MaterialCommunityIcons name="image-outline" size={16} color={colors.teal600} />
            {count > 1 && <Text style={ss.imageButtonCount}>{count}</Text>}
            <View style={ss.imageButtonBadge} pointerEvents="none">
                {pending ? (
                    <ActivityIndicator
                        size="small"
                        color="#FFFFFF"
                        // RN's "small" is 20pt and the badge is 18pt; scaling
                        // keeps it inside the disc without a custom spinner.
                        style={ss.imageButtonSpinner}
                    />
                ) : (
                    <MaterialCommunityIcons name="magnify" size={11} color="#FFFFFF" />
                )}
            </View>
        </Pressable>
    );
}

const ss = StyleSheet.create({
    imageButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        paddingHorizontal: 7,
        paddingVertical: 4,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.teal200,
        backgroundColor: colors.teal50
    },
    imageButtonCount: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: colors.teal600
    },
    imageButtonBadge: {
        position: "absolute",
        top: -7,
        right: -7,
        width: 18,
        height: 18,
        borderRadius: 9,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.teal600,
        // Ring in the card colour so the badge reads as sitting on top of the
        // button rather than merging into its border.
        borderWidth: 1.25,
        borderColor: colors.surface
    },
    imageButtonSpinner: {
        transform: [{ scale: 0.55 }]
    },
    imageButtonWaiting: {
        opacity: 0.6
    }
});
